<template>
  <div class="min-h-screen bg-gray-50 pb-8">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-500">加载中...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error || !data" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <p class="text-red-500 mb-4">{{ error || '未找到溯源信息' }}</p>
        <button @click="fetchTraceDetail" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          重试
        </button>
      </div>
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Header Banner -->
      <img :src="banner" class="w-full">

      <div class="px-4 -mt-8 relative z-20 space-y-4">

        <!-- 1. Product Parameters (商品参数) -->
        <div class="bg-white rounded-xl shadow-sm p-6">
          <div class="text-center mb-6 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">商品参数</h3>
          </div>
          <div class="space-y-3 text-sm">
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">名称：</span>
              <span class="text-gray-800 font-bold text-base">{{ data.productInfo.name }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">品牌：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.brand }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">规格：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.specification }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">批次：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.batch }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">上市：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.releaseDate }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">条码：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.fishCode }}</span>
            </div>
            <div class="flex items-baseline">
              <span class="text-gray-400 w-20 flex-shrink-0">产地：</span>
              <span class="text-gray-800 font-medium">{{ data.productInfo.origin }}</span>
            </div>
          </div>
        </div>

        <!-- 2. Nutritional Components (营养成分) -->
        <div class="bg-white rounded-xl shadow-sm p-6">
          <div class="text-center mb-8 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">营养成分</h3>
          </div>
          <div class="grid grid-cols-3 gap-y-8 gap-x-2 text-center">
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">氨基酸</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.aminoAcids }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">钙</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.calcium }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">铁</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.iron }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">脂肪</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.fat }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">锌</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.zinc }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">铜</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.copper }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">蛋白质</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.protein }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">钾</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.potassium }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 mb-1">硒</span>
              <span class="text-gray-900 font-bold text-lg">{{ data.nutritionalInfo.selenium }}</span>
            </div>
          </div>
        </div>

        <!-- 3. Inspection Report (检测报告) -->
        <div class="bg-white rounded-xl shadow-sm p-6">
          <div class="text-center mb-6 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">检测报告</h3>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="(report, index) in data.testReport" :key="index"
              class="aspect-[4/3] bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
              <img :src="getFullImageUrl(report)" class="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <!-- 4. Farming Process (养殖全过程) -->
        <div class="bg-white rounded-xl shadow-sm p-6">
          <div class="text-center mb-6 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">养殖全过程</h3>
          </div>

          <div class="space-y-4 mb-6 px-2">
            <div class="flex justify-between items-center text-sm border-b border-dashed border-gray-100 pb-3">
              <span class="text-gray-400">养殖周期</span>
              <span class="text-gray-800 font-medium">{{ data.breedingProcess.breedingPeriod }}</span>
            </div>
            <div class="flex justify-between items-center text-sm border-b border-dashed border-gray-100 pb-3">
              <span class="text-gray-400">饲料品牌</span>
              <span class="text-gray-800 font-medium">{{ data.breedingProcess.feedBrand }}</span>
            </div>
            <div class="flex justify-between items-center text-sm pb-1">
              <span class="text-gray-400">喂养方式</span>
              <span class="text-gray-800 font-medium text-right max-w-[60%]">{{ data.breedingProcess.feedingMethod
                }}</span>
            </div>
          </div>

          <!-- Environment Grid -->
          <div class="bg-gray-50 rounded-xl p-5 mb-8">
            <div class="grid grid-cols-3 gap-y-6 gap-x-2 text-center">
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">PH值</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.phValue }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">水温</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.waterTemperature
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">溶解氧</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.dissolvedOxygen
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">电导率</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.electricalConductivity
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">亚硝酸盐</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.nitrite }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">氨氮</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.ammoniaNitrogen
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">余氯</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.residualChlorine
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">重金属</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.heavyMetals
                  }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-xs text-gray-400 mb-1">总磷</span>
                <span class="text-gray-800 font-bold">{{ data.breedingProcess.breedingEnvironment.totalPhosphorus
                  }}</span>
              </div>
            </div>
          </div>

          <!-- Images -->
          <div class="mb-6">
            <div class="text-sm font-medium text-gray-500 mb-3 pl-1">养殖图片：</div>
            <div class="grid grid-cols-2 gap-4">
              <div v-for="(image, index) in data.breedingProcess.breedingImages" :key="index"
                class="aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                <img :src="getFullImageUrl(image)" class="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <!-- Video -->
          <div v-if="data.breedingProcess.breedingVideo?.length">
            <div class="text-sm font-medium text-gray-500 mb-3 pl-1">养殖视频：</div>
            <div class="aspect-video bg-black rounded-lg overflow-hidden shadow-sm">
              <video :src="getFullImageUrl(data.breedingProcess.breedingVideo[0])" controls
                class="w-full h-full object-contain"></video>
            </div>
          </div>
        </div>

        <!-- 5. Farming Base (养殖基地) -->
        <div class="bg-white rounded-xl shadow-sm p-6">
          <div class="text-center mb-6 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">养殖基地</h3>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="(file, index) in data.breedingBase" :key="index"
              class="aspect-[4/3] bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
              <template v-if="isImageFile(file)">
                <img :src="getFullImageUrl(file)" class="w-full h-full object-cover" />
              </template>
              <template v-else-if="isVideoFile(file)">
                <video :src="getFullImageUrl(file)" controls class="w-full h-full object-cover"></video>
              </template>
            </div>
          </div>
        </div>

        <!-- 6. Contact Info (联系方式) -->
        <div class="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div class="text-center mb-8 relative">
            <h3 class="text-lg font-bold text-gray-800 inline-block pb-2 border-b-2 border-gray-100">联系方式</h3>
          </div>

          <div class="space-y-4 mb-8 px-4">
            <div class="flex text-sm items-center">
              <span class="text-gray-400 w-16 flex-shrink-0">官网：</span>
              <span class="text-gray-800 font-medium truncate">www.zjsz-ai.com</span>
            </div>
            <div class="flex text-sm items-center">
              <span class="text-gray-400 w-16 flex-shrink-0">邮箱：</span>
              <span class="text-gray-800 font-medium truncate">yangchangxin@zjszz.cn</span>
            </div>
          </div>

          <div class="flex justify-center gap-12">
            <div class="text-center">
              <div
                class="w-28 h-28 bg-gray-50 rounded-lg mb-3 flex items-center justify-center border border-gray-200 mx-auto">
                <div class="w-24 h-24 bg-white p-1">
                  <img :src="qrcode" alt="公众号" class="w-full h-full opacity-80" />
                </div>
              </div>
              <span class="text-xs text-gray-500">公众号</span>
            </div>
            <div class="text-center">
              <div
                class="w-28 h-28 bg-gray-50 rounded-lg mb-3 flex items-center justify-center border border-gray-200 mx-auto">
                <div class="w-24 h-24 bg-white p-1">
                  <img :src="mp" alt="小程序" class="w-full h-full opacity-80" />
                </div>
              </div>
              <span class="text-xs text-gray-500">小程序</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getTraceDetail } from '@/services/trace'
import type { TraceDetailData } from '@/types/trace'
import qrcode from '@/assets/qrcode.png'
import mp from '@/assets/mp.png'
import banner from '@/assets/banner.jpg'

const route = useRoute()
const traceId = route.params.traceId as string

const data = ref<TraceDetailData | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

const getFullImageUrl = (url: string): string => {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  const imageUrlPrefix = import.meta.env.VITE_IMAGE_URL_PREFIX || ''
  return `${imageUrlPrefix}/${url}`
}

const isImageFile = (url: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  return imageExtensions.some(ext => url.toLowerCase().includes(ext))
}

const isVideoFile = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv']
  return videoExtensions.some(ext => url.toLowerCase().includes(ext))
}

const fetchTraceDetail = async () => {
  if (!traceId) return

  try {
    loading.value = true
    error.value = null
    const response = await getTraceDetail(traceId)
    data.value = response.data
  } catch (err) {
    error.value = '获取溯源信息失败'
    console.error('Error fetching trace detail:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchTraceDetail()
})
</script>
